import { useCallback, useEffect, useState } from "react";
import { IconCheck, IconCalendar } from "../icons";
import logoBranca from "../assets/logo-branca.png";

/**
 * A PÁGINA QUE O CONVIDADO ABRE — /agendar/<token>.
 *
 * Sem login: o token do link é a credencial (mesmo desenho da triagem, do DISC
 * e da pesquisa de clima). A pessoa vê só os horários que sobraram, escolhe um,
 * diz quem é e pronto — a reunião entra na agenda do desenvolvimento com o
 * lembrete junto.
 *
 * O que ela NÃO vê: as outras reuniões, com quem são, nem quantas existem.
 * Horário ocupado simplesmente não aparece na lista.
 */

interface Dia {
  data: string;
  horarios: string[];
}

interface Agenda {
  titulo: string;
  instrucao: string | null;
  duracao_min: number;
  local: string | null;
  link: string | null;
  dias: Dia[];
}

function dataLonga(iso: string): string {
  const [a, m, d] = iso.split("-").map(Number);
  const data = new Date(a, (m ?? 1) - 1, d ?? 1);
  return data.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" });
}

export default function AgendarPublica({ token }: { token: string }) {
  const [agenda, setAgenda] = useState<Agenda | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [dia, setDia] = useState<string | null>(null);
  const [hora, setHora] = useState<string | null>(null);
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [assunto, setAssunto] = useState("");
  const [pauta, setPauta] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [pronto, setPronto] = useState<{ mensagem?: string; local?: string; link?: string } | null>(
    null,
  );

  const carregar = useCallback(async () => {
    try {
      const r = await fetch("/api/agendar/" + encodeURIComponent(token));
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        setErro(body?.error ?? "Não foi possível abrir a agenda.");
        return;
      }
      setAgenda(body);
      setErro(null);
    } catch {
      setErro("Não foi possível abrir a agenda agora. Tente de novo em instantes.");
    }
  }, [token]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  async function confirmar() {
    if (!nome.trim()) {
      setErro("Informe o seu nome.");
      return;
    }
    if (!dia || !hora) {
      setErro("Escolha o dia e o horário.");
      return;
    }
    setEnviando(true);
    setErro(null);
    try {
      const r = await fetch("/api/agendar/" + encodeURIComponent(token), {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ nome, email, telefone, assunto, pauta, data: dia, hora }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        // 409 = o horário foi tomado enquanto a página estava aberta: o servidor
        // já manda a lista nova, então a pessoa escolhe outro sem recarregar.
        if (body?.dias) {
          setAgenda((a) => (a ? { ...a, dias: body.dias } : a));
          setHora(null);
        }
        setErro(body?.error ?? "Não foi possível enviar o pedido.");
        return;
      }
      setPronto(body);
    } catch {
      setErro("Não foi possível enviar agora. Tente de novo em instantes.");
    } finally {
      setEnviando(false);
    }
  }

  const diaEscolhido = agenda?.dias.find((d) => d.data === dia) ?? null;

  return (
    <div className="pub">
      <header className="pub-topo">
        <img src={logoBranca} alt="SERCAMP" />
        <span>SERCAMP · Desenvolvimento de Software</span>
      </header>

      <main className="pub-corpo">
        {pronto ? (
          <div className="pub-card pub-fim">
            <span className="pub-ok">
              <IconCheck size={28} />
            </span>
            <h1>Pedido enviado!</h1>
            <p>{pronto.mensagem}</p>
            {pronto.local && (
              <p className="muted">
                <strong>Local:</strong> {pronto.local}
              </p>
            )}
            {pronto.link && (
              <p className="muted">
                <strong>Chamada:</strong>{" "}
                <a href={pronto.link} target="_blank" rel="noreferrer">
                  {pronto.link}
                </a>
              </p>
            )}
            <p className="muted">
              O Desenvolvimento confirma o horário e avisa pelo contato informado. Pode fechar esta
              página.
            </p>
          </div>
        ) : !agenda ? (
          <div className="pub-card">
            <p>{erro ?? "Carregando os horários…"}</p>
          </div>
        ) : (
          <div className="pub-card">
            <h1>{agenda.titulo}</h1>
            <p className="muted">
              Reuniões de {agenda.duracao_min} minutos. Escolha o dia e o horário que preferir.
              {agenda.local ? " Local: " + agenda.local + "." : ""}
            </p>
            {agenda.instrucao && <p className="pub-instrucao">{agenda.instrucao}</p>}

            {agenda.dias.length === 0 ? (
              <p className="pub-vazio">
                Não há horários disponíveis no momento. Fale com o desenvolvedor pelo WhatsApp.
              </p>
            ) : (
              <>
                <h2>
                  <IconCalendar size={16} /> Escolha o dia
                </h2>
                <div className="pub-dias">
                  {agenda.dias.map((d) => (
                    <button
                      type="button"
                      key={d.data}
                      className={"pub-dia" + (dia === d.data ? " on" : "")}
                      onClick={() => {
                        setDia(d.data);
                        setHora(null);
                      }}
                    >
                      <strong>{dataLonga(d.data)}</strong>
                      <small>{d.horarios.length} horário(s)</small>
                    </button>
                  ))}
                </div>

                {diaEscolhido && (
                  <>
                    <h2>Escolha o horário</h2>
                    <div className="pub-horas">
                      {diaEscolhido.horarios.map((h) => (
                        <button
                          type="button"
                          key={h}
                          className={"pub-hora" + (hora === h ? " on" : "")}
                          onClick={() => setHora(h)}
                        >
                          {h}
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {hora && (
                  <>
                    <h2>Quem vai participar</h2>
                    <div className="pub-form">
                      <label>
                        <span>Seu nome *</span>
                        <input value={nome} onChange={(e) => setNome(e.target.value)} autoFocus />
                      </label>
                      <label>
                        <span>Seu e-mail</span>
                        <input
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          inputMode="email"
                        />
                      </label>
                      <label>
                        <span>Seu WhatsApp</span>
                        <input
                          value={telefone}
                          onChange={(e) => setTelefone(e.target.value)}
                          inputMode="tel"
                        />
                      </label>
                      <label>
                        <span>Assunto</span>
                        <input
                          value={assunto}
                          onChange={(e) => setAssunto(e.target.value)}
                          placeholder="Ex.: ajuste na tela de colaboradores"
                        />
                      </label>
                      <label className="pub-wide">
                        <span>O que você precisa tratar</span>
                        <textarea rows={3} value={pauta} onChange={(e) => setPauta(e.target.value)} />
                      </label>
                    </div>

                    {erro && <p className="pub-erro">{erro}</p>}

                    <button className="pub-btn" onClick={confirmar} disabled={enviando}>
                      {enviando
                        ? "Enviando…"
                        : "Pedir " + dataLonga(dia!) + " às " + hora}
                    </button>
                  </>
                )}

                {!hora && erro && <p className="pub-erro">{erro}</p>}
              </>
            )}
          </div>
        )}
      </main>

      <footer className="pub-rodape">SERCAMP Serviços · Completa em Manutenção e Prevenção Elétrica</footer>
    </div>
  );
}
